/** Cada indicador do cockpit do Workflow leva à tela com os itens que compõem o número, já filtrados. */
export type IndicadorCockpit =
  | 'AGUARDANDO_APROVACAO' | 'EM_ATRASO_SLA' | 'REJEITADAS' | 'AGUARDANDO_COMPLEMENTO'
  | 'APROVADAS' | 'LANCAMENTOS_MANUAIS' | 'RISCO_ALTO';

export interface ContextoCockpit {
  /** Empresa e período selecionados no filtro do dashboard ("TODOS"/vazio = sem período). */
  empresaId?: string;
  periodo?: string;
}

export interface DestinoCockpit {
  path: string;
  state: { empresaId?: string; periodo?: string; status?: string; risco?: string; tab?: string };
}

const BASE = '/workflow-aprovacao';

export const destinoDoIndicador = (indicador: IndicadorCockpit, ctx: ContextoCockpit = {}): DestinoCockpit => {
  const contexto = {
    ...(ctx.empresaId ? { empresaId: ctx.empresaId } : {}),
    ...(ctx.periodo && ctx.periodo !== 'TODOS' ? { periodo: ctx.periodo } : {}),
  };
  switch (indicador) {
    case 'AGUARDANDO_APROVACAO':    return { path: `${BASE}/conciliacoes`, state: { ...contexto, status: 'AGUARDANDO_APROVACAO' } };
    case 'REJEITADAS':              return { path: `${BASE}/conciliacoes`, state: { ...contexto, status: 'REJEITADA' } };
    case 'AGUARDANDO_COMPLEMENTO':  return { path: `${BASE}/conciliacoes`, state: { ...contexto, status: 'EM_CONFERENCIA' } };
    case 'APROVADAS':               return { path: `${BASE}/conciliacoes`, state: { ...contexto, status: 'APROVADA' } };
    case 'RISCO_ALTO':              return { path: `${BASE}/conciliacoes`, state: { ...contexto, risco: 'ALTO' } };
    case 'EM_ATRASO_SLA':           return { path: `${BASE}/monitoramento`, state: { ...contexto, tab: 'VENCIDAS' } };
    case 'LANCAMENTOS_MANUAIS':     return { path: `${BASE}/lancamentos-manuais`, state: contexto };
  }
};

// ── Destino automático: leva direto ao item (ou à lista) que está afetado ─────────────────────

export interface ConciliacaoCockpit { id: number; empresaId: number | string; periodo: string; status: string; risco: string }

export interface DadosCockpit {
  conciliacoes: ConciliacaoCockpit[];
  /** Ids das conciliações com prazo de SLA vencido (do monitoramento). */
  vencidasIds: number[];
}

const afetadasPor = (indicador: IndicadorCockpit, d: DadosCockpit): ConciliacaoCockpit[] => {
  switch (indicador) {
    case 'AGUARDANDO_APROVACAO':   return d.conciliacoes.filter(c => c.status === 'AGUARDANDO_APROVACAO');
    case 'REJEITADAS':             return d.conciliacoes.filter(c => c.status === 'REJEITADA');
    case 'AGUARDANDO_COMPLEMENTO': return d.conciliacoes.filter(c => c.status === 'EM_CONFERENCIA');
    case 'APROVADAS':              return d.conciliacoes.filter(c => c.status === 'APROVADA');
    case 'RISCO_ALTO':             return d.conciliacoes.filter(c => c.risco === 'ALTO');
    case 'EM_ATRASO_SLA':          return d.conciliacoes.filter(c => d.vencidasIds.includes(c.id));
    case 'LANCAMENTOS_MANUAIS':    return [];
  }
};

const maisFrequente = (valores: string[]): string | undefined => {
  const cont = new Map<string, number>();
  valores.forEach(v => cont.set(v, (cont.get(v) ?? 0) + 1));
  let melhor: string | undefined;
  cont.forEach((n, v) => { if (melhor === undefined || n > (cont.get(melhor) ?? 0)) melhor = v; });
  return melhor;
};

/**
 * Ao clicar no indicador vai direto ao que está afetado:
 *  - só uma conciliação afetada → abre o detalhe dela;
 *  - várias → abre a lista (ou o monitoramento) já com a empresa e o período onde elas estão e o filtro aplicado,
 *    mesmo que o dashboard esteja sem empresa/período selecionados.
 */
export const resolverDestinoCockpit = (indicador: IndicadorCockpit, ctx: ContextoCockpit, dados: DadosCockpit): DestinoCockpit => {
  const empresaCtx = ctx.empresaId || undefined;
  const periodoCtx = ctx.periodo && ctx.periodo !== 'TODOS' ? ctx.periodo : undefined;

  let afetadas = afetadasPor(indicador, dados);
  if (empresaCtx) afetadas = afetadas.filter(c => String(c.empresaId) === empresaCtx);
  if (empresaCtx && periodoCtx) afetadas = afetadas.filter(c => c.periodo === periodoCtx);

  if (afetadas.length === 1) return { path: `${BASE}/conciliacoes/${afetadas[0].id}`, state: {} };

  const base = afetadas.length > 0 ? afetadas : dados.conciliacoes;
  const empresaId = empresaCtx ?? maisFrequente(base.map(c => String(c.empresaId)));
  const doEmpresa = base.filter(c => String(c.empresaId) === empresaId);
  const periodo = periodoCtx ?? maisFrequente(doEmpresa.map(c => c.periodo));
  return destinoDoIndicador(indicador, { empresaId, periodo });
};
